import { Box, Typography, Button } from "@mui/material";
import { VisibilityOutlined, CheckCircleOutline } from "@mui/icons-material";
import { colors } from "../../../../ui/colors";
import { useInviteIndividual } from "../hooks/useInviteIndividual";
import { useState } from "react";
import { useModals } from "../../../../../context/ModalContext";
import FlownectLogo from "../../../../../assets/images/Flownect.webp";

export const InviteUserView = ({ company, workforce }) => {
  const { workforceMutation } = useInviteIndividual();
  const [showSuccess, setShowSuccess] = useState(false);
  const { closeByKey } = useModals();

  const handleSendInvitation = () => {
    const payload = {
      uid: workforce.uid,
      target: "workforce_invitation",
    };

    workforceMutation.mutate(payload, {
      onSuccess: () => {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 2800);

        setTimeout(() => {
          closeByKey(`Invite-User-${workforce.uid}`);
        }, 2500);
      },
      onError: (err) => {
        console.error("Error sending invitation:", err);
      },
    });
  };

  if (!company) return null;

  return (
    <Box
      sx={{
        position: "relative",
        maxWidth: 600,
        height: "100%",
        mx: "auto",
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {showSuccess && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: colors.success[300] + "CC",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            flexDirection: "column",
            animation: "fadeInOut 3s ease forwards",
            "@keyframes fadeInOut": {
              "0%": { opacity: 0 },
              "10%": { opacity: 1 },
              "80%": { opacity: 1 },
              "100%": { opacity: 0 },
            },
          }}
        >
          <CheckCircleOutline
            sx={{
              fontSize: 120,
              color: "#fff",
              animation: "popScale 1s ease forwards",
              "@keyframes popScale": {
                "0%": { transform: "scale(0)", opacity: 0 },
                "40%": { transform: "scale(1.3)", opacity: 1 },
                "70%": { transform: "scale(0.95)", opacity: 1 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: "#fff",
              fontWeight: 700,
              mt: 2,
              opacity: 0,
              animation: "fadeText 1.2s ease 0.6s forwards",
              "@keyframes fadeText": {
                from: { opacity: 0, transform: "translateY(10px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            Invitation Sent!
          </Typography>
        </Box>
      )}

      {/* TEMPLATE LABEL */}
      <Box
        sx={{
          background: colors.gray[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          py: 1,
          borderBottom: `1px solid ${colors.gray[200]}`,
          flexShrink: 0,
        }}
      >
        <VisibilityOutlined sx={{ fontSize: 18, color: colors.gray[600] }} />
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: colors.gray[600],
            letterSpacing: 0.4,
            textTransform: "uppercase",
          }}
        >
          Email Template Preview
        </Typography>
      </Box>

      {/* HEADER */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #FB8E00 0%, #FFD900 100%)",
          p: 3,
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        <Box component="img" src={FlownectLogo} alt="Flownect" sx={{ width: 150, height: "auto", mb: 1.5 }} />
        <Typography
          variant="h5"
          sx={{
            color: "#fff",
            fontWeight: 700,
            textShadow: "0 2px 4px rgba(0,0,0,0.15)",
          }}
        >
          You’re Invited! 🎉
        </Typography>
      </Box>

      {/* BODY */}
      <Box
        sx={{
          flex: 1,
          p: 4,
          overflowY: "auto",
          background: "linear-gradient(180deg, var(--fx-surface-base, #FFFFFF) 0%, var(--fx-surface-base, #FFFFFF) 100%)",
        }}
      >
        <Typography variant="h6" align="center" sx={{ mb: 1, fontWeight: 700, color: colors.gray[900] }}>
          Hi {`${workforce.firstName} ${workforce.lastName}` || "User"}!
        </Typography>

        <Typography align="center" sx={{ color: colors.gray[600], fontSize: 15, mb: 3 }}>
          <strong style={{ color: colors.orange.base }}>{company?.businessName || "Your Company"}</strong> has invited you to join their team on{" "}
          <strong>Flownect</strong>.
        </Typography>

        <Box
          sx={{
            border: `2px solid ${colors.orange[200]}`,
            borderRadius: 2,
            p: 2.5,
            mb: 3,
            backgroundColor: colors.orange[50],
            boxShadow: "0 1px 4px rgba(251,142,0,0.05)",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, color: colors.gray[800], fontWeight: 600 }}>
            What’s next?
          </Typography>
          <Typography variant="body2" sx={{ color: colors.gray[600], lineHeight: 1.7 }}>
            • Click the button below to accept your invitation. <br />
            • Set up your account in a few steps. <br />• Start collaborating with your team right away.
          </Typography>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box
        sx={{
          backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
          borderTop: `1px solid ${colors.gray[200]}`,
          p: 2.5,
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSendInvitation}
          disabled={workforceMutation.isPending}
          sx={{
            width: "80%",
            background: "linear-gradient(135deg, #FB8E00 0%, #FFD900 100%)",
            color: "#fff",
            fontWeight: 700,
            py: 1.4,
            borderRadius: 2,
            boxShadow: "0 6px 16px rgba(251,142,0,0.3)",
            textTransform: "none",
            "&:hover": {
              background: "linear-gradient(135deg, #FFA733 0%, #FFDE55 100%)",
            },
          }}
        >
          {workforceMutation.isPending ? "Sending..." : "SEND INVITATION"}
        </Button>
      </Box>
    </Box>
  );
};
